import type React from "react";
const PILL_FOCUS =
  "focus-ring";

/**
 * The house pill toggle — one styling for the programme scope switcher, the
 * import-source tabs and future segmented pickers. `role="radio"` rows (one
 * pick of a radiogroup) announce via aria-checked; plain toggles via
 * aria-pressed. `count` renders the trailing muted counter, `dotColor` an
 * 8px leading track dot (deterministic programme colors).
 */
export function PillToggle({
  label,
  active,
  onClick,
  role,
  count,
  dotColor,
  disabled = false,
  tabIndex,
  onKeyDown,
  buttonRef,
  testId,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  /** "radio" inside a role="radiogroup" container; omit for a plain toggle. */
  role?: "radio";
  count?: number;
  /** CSS color for the leading 8px dot (e.g. `var(--color-status-info)`). */
  dotColor?: string;
  /** Locked pick (e.g. the API-immutable program-item kind on edit) — still
   *  announces its checked state, just can't be operated. */
  disabled?: boolean;
  /** Roving tab stop inside a radiogroup (0 for the checked radio, -1 else). */
  tabIndex?: number;
  /** Arrow-key handling of the enclosing radiogroup. */
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
  /** For a radiogroup that moves focus with the arrow keys. */
  buttonRef?: React.Ref<HTMLButtonElement>;
  testId?: string;
}) {
  return (
    <button
      ref={buttonRef}
      type="button"
      role={role}
      disabled={disabled}
      tabIndex={tabIndex}
      onKeyDown={onKeyDown}
      data-testid={testId}
      {...(role === "radio"
        ? { "aria-checked": active }
        : { "aria-pressed": active })}
      onClick={onClick}
      className={`inline-flex items-center px-3 py-1.5 rounded-full border body-3-medium transition-colors ${PILL_FOCUS} disabled:opacity-50 disabled:pointer-events-none ${
        active
          ? "outline-tertiary surface-tertiary font-primary"
          : "outline-quaternary font-secondary hover:surface-secondary"
      }`}
    >
      {dotColor && (
        <span
          aria-hidden
          className="h-2 w-2 rounded-full mr-1.5 shrink-0"
          style={{ backgroundColor: dotColor }}
        />
      )}
      {label}
      {count !== undefined && (
        <span
          className={`ml-1.5 body-4-regular ${
            active ? "font-secondary" : "font-tertiary"
          }`}
        >
          {count}
        </span>
      )}
    </button>
  );
}
